import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Guid, Nillable, valueOrDefault } from '@web/utils';
import { AdminShipmentSizeType } from '../../enums/admin-shipment-size-type';
import { Button } from 'primeng/button';
import {
  AlertsComponent,
  AlertsService,
  ConfirmationService,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengSelectDirective,
  PrimengTextareaDirective,
  SelectItem,
  ToastService,
} from '@web/shared/ui';
import { AdminOrderDataSourceService } from '../../services/admin-order-data-source.service';
import {
  AdminAdditionalShippingData,
  AdminAdditionalShippingFormGroup,
} from '../../models/admin-additional-shipping-data';
import { HttpErrorService } from '@web/common';
import { Select } from 'primeng/select';
import { Observable } from 'rxjs';
import { AdminShipmentSizeTypeProviderService } from '../../providers/admin-shipment-size-type-provider.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Textarea } from 'primeng/textarea';
import { DatePicker } from 'primeng/datepicker';
import { AdminOrderShipmentItemsControlComponent } from '../admin-order-shipment-items-control/admin-order-shipment-items-control.component';
import { AdminOrderShipmentFormItem } from '../../models/admin-order-shipment-form-item';
import { AdminOrderShipmentComponentGroupType } from '../../enums/admin-order-shipment-component-group-type';

@Component({
  selector: 'app-admin-additional-shipping',
  imports: [
    CommonModule,
    Button,
    PrimengButtonDirective,
    AlertsComponent,
    FormControlComponent,
    Select,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Textarea,
    PrimengTextareaDirective,
    DatePicker,
    AdminOrderShipmentItemsControlComponent,
  ],
  templateUrl: './admin-additional-shipping.component.html',
  styleUrl: './admin-additional-shipping.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminAdditionalShippingComponent {
  public readonly orderId = input.required<Guid>();

  public readonly submitEvent = output();
  public readonly cancelEvent = output();

  protected readonly form: AdminAdditionalShippingFormGroup;
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly shipmentItemType = AdminOrderShipmentComponentGroupType;
  /* prettier-ignore */
  protected readonly shipmentSizeSelectItems$: Observable<SelectItem<AdminShipmentSizeType>[]>;

  private readonly defaultSizeType = AdminShipmentSizeType.Small;
  private readonly orderDataSource = inject(AdminOrderDataSourceService);
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly shipmentSizeTypeProvider = inject(AdminShipmentSizeTypeProviderService);
  private readonly toast = inject(ToastService);
  private readonly alerts = inject(AlertsService);
  private readonly httpError = inject(HttpErrorService);
  private readonly confirmation = inject(ConfirmationService);

  public constructor() {
    this.form = this.buildForm();
    this.shipmentSizeSelectItems$ = this.shipmentSizeTypeProvider.selectItems();
  }

  public toData(): AdminAdditionalShippingData {
    const data = this.form.getRawValue();

    return new AdminAdditionalShippingData({
      size: valueOrDefault(data.size, this.defaultSizeType),
      scheduledShippingDate: data.scheduledShippingDate,
      comment: data.comment,
      boxes: valueOrDefault(data.boxes, []),
      products: valueOrDefault(data.products, []),
      cosmetics: valueOrDefault(data.cosmetics, []),
    });
  }

  public handleCancelButtonClick(): void {
    this.cancelEvent.emit();
  }

  public handleSubmitButtonClick(): void {
    this.confirmation.show((builder) =>
      builder
        .title('Potwierdzenie')
        .message('Czy na pewno chcesz dodać przesyłkę do zamówienia?')
        .accept(() => this.createAdditionalShipment())
        .build(),
    );
  }

  private createAdditionalShipment(): void {
    this.orderDataSource
      .createAdditionalShipmentByData({
        orderId: this.orderId(),
        data: this.toData(),
      })
      .subscribe({
        next: () => {
          this.showSuccessToast();
          this.submitEvent.emit();
        },
        error: (error) => this.showFailureAlertByResponse(error),
      });
  }

  private showSuccessToast(): void {
    this.toast.show((builder) =>
      builder.initSuccess('Przesyłka została dodana do zamówienia.').build(),
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  /* prettier-ignore */
  private buildForm(): AdminAdditionalShippingFormGroup {
    return this.builder.group({
      size: this.builder.control<Nillable<AdminShipmentSizeType>>(this.defaultSizeType, [Validators.required]),
      scheduledShippingDate: this.builder.control<Nillable<Date>>(null),
      comment: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminAdditionalShippingData.CommentMaxLength)]),
      boxes: this.builder.nonNullable.control<AdminOrderShipmentFormItem[]>([]),
      products: this.builder.nonNullable.control<AdminOrderShipmentFormItem[]>([]),
      cosmetics: this.builder.nonNullable.control<AdminOrderShipmentFormItem[]>([]),
    });
  }
}
